import { useEffect, useState } from "react";
import { Sparkles, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { streamPost } from "@/lib/api";
import { copyText } from "@/lib/clipboard";

const STYLES = [
  { key: "payer", label: "For reauthorization" },
  { key: "caregiver", label: "For caregivers" },
];

export function ProgressSummary({ clientId, range }) {
  const [style, setStyle] = useState("payer");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(""); }, [range.start, range.end]);

  const run = async () => {
    setBusy(true);
    setText("");
    try {
      const full = await streamPost(`/clients/${clientId}/progress-summary`, { ...range, style }, setText);
      setText(full.trim());
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border bg-white overflow-hidden break-inside-avoid" data-testid="progress-summary-card">
      <div className="no-print bg-[#0B3F44] text-white p-5 flex flex-wrap items-center justify-between gap-4 relative grain">
        <div className="relative">
          <div className="font-display font-semibold text-lg">AI progress summary</div>
          <div className="text-sm text-teal-100/80">Written from the charts below — only your data, nothing invented.</div>
        </div>
        <div className="relative flex flex-wrap items-center gap-2">
          <div className="flex rounded-full bg-white/10 p-1">
            {STYLES.map((s) => (
              <button key={s.key} onClick={() => setStyle(s.key)} data-testid={`summary-style-${s.key}`}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${style === s.key ? "bg-white text-primary" : "text-teal-50 hover:bg-white/10"}`}>
                {s.label}
              </button>
            ))}
          </div>
          <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" onClick={run} disabled={busy} data-testid="summary-generate-button">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}{text ? "Rewrite" : "Write summary"}
          </Button>
        </div>
      </div>
      {(text || busy) && (
        <div className="p-5 space-y-3">
          <h2 className="hidden print:block text-lg font-semibold">Progress summary</h2>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} readOnly={busy} placeholder="Writing…"
            className="no-print min-h-[200px] text-[15px] leading-relaxed" data-testid="summary-textarea" />
          <p className="hidden print:block text-[14px] leading-relaxed whitespace-pre-wrap">{text}</p>
          <div className="no-print flex justify-end">
            <Button variant="outline" className="rounded-full" disabled={busy || !text} data-testid="summary-copy-button"
              onClick={() => copyText(text, "Summary copied — ready to paste")}>
              <Copy className="h-4 w-4 mr-2" />Copy
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
